import * as Sentry from '@sentry/react';
import { useSelector } from 'react-redux';
import { Routes } from '../../../../routes.types';
import { PopOverType } from '../../../../redux/tickets/user.actions';
import { transformTicketsToLineItems } from '../../../../services/tickets/transformTicketsToLineItems';
import { findItemByLinePath } from '../../../../services/tickets/findItemByLinePath';
import { findAllItemsByGroupKey } from '../../../../services/tickets/findAllItemsByGroupKey';
import { sortLinesByIndex } from '../../utils';
import { groupTicketLines } from '../../../../services/tickets/groupTicketLines';
import { KdsTicket, KdsTicketGroup, TicketStatus } from '../../../../redux/tickets/types/ticket';
import { KdsLineItem, KdsLineItemBlock } from '../../../../redux/tickets/types/kds-line-item';
import { createTicketLineBlocks } from '../../../../services/tickets/createTicketLineBlocks';
import { activeStatusComparator } from '../../../../redux/tickets/middlewares/activeStatusComparator';
import { getGroupedDisplayEnabled } from '../../../../redux/localConfig/selector';
import { flattenLineItemsToLeaves } from '../../../../services/tickets/flattenAndGroupLinesBySku';

type PropsVm = {
    ticket: KdsTicketGroup;
    dispatchGroupedTicketClicked: (orderIds: string[]) => void;
    dispatchGroupedLineClicked: (clickedPath: string, lines: { orderId: string; linePath: string }[]) => void;
    dispatchLineLongPressed: (orderId: string, linePath: string) => void;
    dispatchGroupedUndisplayTickets: (orderIds: string[]) => void;
    dispatchStatusChange: (orderIds: string[], type: PopOverType, route: Routes) => void;
};

type ReturnTypeVm = {
    orderIds: string[];
    ticketNumbers: string[];
    toCancelTickets: KdsTicket[];
    modifiedTickets: KdsTicket[];
    groupedTicket: KdsTicket;
    ticketBlocks: KdsLineItemBlock[];
    onGroupedTicketClick: (orderIds: string[]) => void;
    onGroupedLineClick: (clickedLinePath: string) => void;
    onGroupedLineLongPress: (clickedLinePath: string) => void;
    onGroupedTicketsUndisplay: (orderIds: string[]) => void;
    onPopOverStatusChange: (orderIds: string[], type: PopOverType, route: Routes) => void;
};

function getGroupedInfoFromTickets(tickets: KdsTicket[]): KdsTicket {
    return tickets.reduce((prevTicket: KdsTicket, currTicket: KdsTicket) => {
        const { status: prevStatus } = prevTicket;
        const { status: currStatus } = currTicket;
        const newTicket: KdsTicket = { ...prevTicket };

        if (!Object.values(currTicket.kdsLines).length) {
            Sentry.captureEvent({
                message: `Ticket ${currTicket.ticketNumber} of grouped ticket arrived without line`,
                extra: {
                    brandId: currTicket.brandId,
                    orderId: currTicket.orderId,
                    restaurantId: currTicket.restaurantId,
                    ticketNumber: currTicket.ticketNumber,
                },
            });
        }

        newTicket.ticketNumber = prevTicket.groupingKey ?? prevTicket.ticketNumber;
        newTicket.customerFirstName = null;
        newTicket.customerLastName = null;

        if (newTicket.tableName == null) {
            newTicket.tableName = currTicket.tableName;
        }
        if (newTicket.updatedAt != null && currTicket.updatedAt != null && currTicket.updatedAt > newTicket.updatedAt) {
            newTicket.updatedAt = currTicket.updatedAt;
        }
        if (currTicket.expectedAt > newTicket.expectedAt) {
            newTicket.expectedAt = currTicket.expectedAt;
        }

        const [groupedStatus] = [prevStatus, currStatus].sort(activeStatusComparator);
        newTicket.status = groupedStatus;

        return newTicket;
    }, tickets[0]);
}

export const useGroupedTicketVm = ({
    ticket,
    dispatchGroupedTicketClicked,
    dispatchGroupedLineClicked,
    dispatchLineLongPressed,
    dispatchGroupedUndisplayTickets,
    dispatchStatusChange,
}: PropsVm): ReturnTypeVm => {
    const { tickets = [] } = ticket;
    const orderIds = tickets.map((t) => t.orderId);
    const ticketNumbers = tickets.map((t) => t.ticketNumber);
    const toCancelTickets = tickets.filter((t) => t.status === TicketStatus.ToCancel);
    const modifiedTickets = tickets.filter((t) => t.status === TicketStatus.Modified);
    const groupedTicket = getGroupedInfoFromTickets(ticket.tickets);

    const groupedDisplayEnabled = useSelector(getGroupedDisplayEnabled);

    const lineItems: KdsLineItem[] = transformTicketsToLineItems(tickets);
    const sortedLineItems = sortLinesByIndex(lineItems);
    const processedItems: KdsLineItem[] = groupedDisplayEnabled
        ? flattenLineItemsToLeaves(sortedLineItems)
        : sortedLineItems;

    const allLineBlocks: KdsLineItemBlock[] = createTicketLineBlocks(processedItems);

    const allGroupedLineBlocks = allLineBlocks.map((block) => {
        const groupedBlock = { ...block };
        groupedBlock.lines = groupTicketLines(groupedBlock.lines);

        return groupedBlock;
    });

    const findGroupedLineByUuid = (clickedLinePath: string): KdsLineItem | undefined => {
        const parts = clickedLinePath.split('.');
        const clickedUuid = parts[parts.length - 1];
        return processedItems.find((item) => item.uuid === clickedUuid);
    };

    const getRelatedGroupedLines = (groupingKey: string, blockKey?: string) =>
        processedItems.filter((item) => item.groupingKey === groupingKey && item.blockKey === blockKey);

    const onGroupedLineClick = (clickedLinePath: string) => {
        if (groupedDisplayEnabled) {
            const clickedLine = findGroupedLineByUuid(clickedLinePath);
            if (clickedLine) {
                const relatedItems = getRelatedGroupedLines(clickedLine.groupingKey as string, clickedLine.blockKey);
                const lines = relatedItems.map(({ linePath, orderId: itemOrderId }) => ({
                    linePath: linePath as string,
                    orderId: itemOrderId,
                }));
                dispatchGroupedLineClicked(clickedLine.linePath as string, lines);
            }
            return;
        }

        const clickedLine = findItemByLinePath({ kdsLines: processedItems } as unknown as KdsLineItem, clickedLinePath);
        if (clickedLine) {
            const relatedItems = findAllItemsByGroupKey(
                { kdsLines: processedItems } as unknown as KdsLineItem,
                clickedLine.groupingKey as string,
            );
            const lines = relatedItems.map(({ linePath, orderId: itemOrderId }) => ({
                linePath: linePath as string,
                orderId: itemOrderId,
            }));
            dispatchGroupedLineClicked(clickedLinePath, lines);
        }
    };

    const onGroupedLineLongPress = (clickedLinePath: string) => {
        if (groupedDisplayEnabled) {
            const clickedLine = findGroupedLineByUuid(clickedLinePath);
            if (clickedLine) {
                const relatedItems = getRelatedGroupedLines(clickedLine.groupingKey as string, clickedLine.blockKey);
                relatedItems.forEach(({ linePath: originalPath, orderId: itemOrderId }) => {
                    dispatchLineLongPressed(itemOrderId, originalPath as string);
                });
            }
            return;
        }

        const clickedLine = findItemByLinePath({ kdsLines: processedItems } as unknown as KdsLineItem, clickedLinePath);
        if (clickedLine) {
            const relatedItems = findAllItemsByGroupKey(
                { kdsLines: processedItems } as unknown as KdsLineItem,
                clickedLine.groupingKey as string,
            );
            relatedItems.forEach(({ linePath, orderId: itemOrderId }) => {
                dispatchLineLongPressed(itemOrderId, linePath as string);
            });
        }
    };

    return {
        orderIds,
        ticketNumbers,
        toCancelTickets,
        modifiedTickets,
        groupedTicket,
        ticketBlocks: allGroupedLineBlocks,
        onGroupedTicketClick: dispatchGroupedTicketClicked,
        onGroupedLineClick,
        onGroupedLineLongPress,
        onGroupedTicketsUndisplay: dispatchGroupedUndisplayTickets,
        onPopOverStatusChange: dispatchStatusChange,
    };
};
